import React, { useState, useEffect } from 'react';
import { useAuth } from '@/lib';
import { useBusinessIdea } from '../hooks/useBusinessIdea';
import { getTaskStage, getTaskModule, getTaskById } from '@orbit/shared/content-hierarchy';
import { TaskProgress, BusinessIdea } from '@orbit/shared/types';
import { BackToHome } from '../components';
import { useNavigate } from 'react-router-dom';

const CompletedTasksPage: React.FC = () => {
  const { user } = useAuth();
  const { businessIdea, loading, error } = useBusinessIdea();
  const navigate = useNavigate();

  // Helper function to safely parse dates from Firestore
  const parseDate = (dateValue: any): Date => {
    if (!dateValue) return new Date();

    if (dateValue instanceof Date) {
      return dateValue;
    }

    if (typeof dateValue === 'string') {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? new Date() : parsed;
    }

    // Handle Firestore Timestamp
    if (dateValue && typeof dateValue === 'object' && dateValue.toDate) {
      return dateValue.toDate();
    }

    // Handle timestamp number
    if (typeof dateValue === 'number') {
      return new Date(dateValue);
    }

    return new Date();
  };
  const [completedTasks, setCompletedTasks] = useState<
    Array<{
      taskId: string;
      taskProgress: TaskProgress;
      title: string;
      description: string;
      stage: string;
      module: string;
      completedAt: Date;
    }>
  >([]);

  useEffect(() => {
    const businessIdeaWithProgress = businessIdea as BusinessIdea & {
      taskProgress?: Record<string, Record<string, TaskProgress>>;
    };

    if (businessIdeaWithProgress?.taskProgress && user?.id) {
      const completed = Object.entries(businessIdeaWithProgress.taskProgress)
        .map(([taskId, taskProgressRecord]) => {
          // Get the current user's task progress
          const userTaskProgress = taskProgressRecord?.[user.id];

          // Only include if this user has completed this task
          if (!userTaskProgress || userTaskProgress.status !== 'completed') {
            return null;
          }

          const stage = getTaskStage(taskId);
          const module = getTaskModule(taskId);

          // Get task details from content hierarchy
          const taskDetails = getTaskById(taskId);

          return {
            taskId,
            taskProgress: userTaskProgress,
            title: taskDetails?.title || taskId,
            description: taskDetails?.description || '',
            stage: stage,
            module: module,
            completedAt: parseDate(userTaskProgress.completedAt),
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null) // Remove null entries with type guard
        .sort((a, b) => b.completedAt.getTime() - a.completedAt.getTime()); // Most recent first

      setCompletedTasks(completed);
    }
  }, [businessIdea, user?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-dark">
        <BackToHome />
        <main className="p-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-700 rounded w-1/3 mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, index) => (
                  <div
                    key={index}
                    className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-4"
                  >
                    <div className="h-5 bg-gray-700 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded w-5/6"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background-dark">
        <BackToHome />
        <main className="p-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="bg-red-900/20 backdrop-blur-sm border border-red-500 rounded-lg p-6">
              <p className="text-red-400">Failed to load completed tasks: {error}</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-dark">
      <BackToHome />
      <main className="p-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold font-heading text-white mb-6">Completed Tasks</h1>

          {completedTasks.length === 0 ? (
            <div className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-8 text-center">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-xl font-semibold text-white mb-2">No completed tasks yet</h3>
              <p className="text-text-secondary">Complete your first task to see it appear here!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-6">
                <h2 className="text-xl font-semibold text-white mb-2">Progress Summary</h2>
                <p className="text-text-secondary">
                  You've completed{' '}
                  <span className="text-white font-semibold">{completedTasks.length}</span> tasks
                  across {new Set(completedTasks.map((t) => t.module)).size} modules.
                </p>
              </div>

              {/* Completed Tasks Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {completedTasks.map((task) => (
                  <div
                    key={task.taskId}
                    className="group bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-4 hover:border-blue-500 hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer relative overflow-hidden"
                    onClick={() => navigate(`/task/${task.taskId}`)}
                  >
                    {/* Subtle outline on hover */}
                    <div className="absolute inset-0 rounded-lg border-2 border-transparent group-hover:border-blue-500/20 transition-colors duration-200 pointer-events-none"></div>

                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="text-white font-semibold text-bodyM line-clamp-2 flex-1 group-hover:text-blue-300 transition-colors duration-200">
                        {task.title}
                      </h3>
                      <div className="flex-shrink-0 flex items-center justify-end w-8">
                        <div className="w-2 h-2 bg-green-500 rounded-full group-hover:-translate-x-5 transition-transform duration-200"></div>
                        <svg
                          className="w-4 h-4 text-blue-400 opacity-0 group-hover:opacity-100 transition-all duration-200 absolute"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>

                    <p className="text-text-secondary text-sm mb-3 group-hover:text-text-primary transition-colors duration-200">
                      {task.description}
                    </p>

                    <div className="flex items-center justify-between text-xs text-text-secondary">
                      <span className="bg-gray-700/50 px-2 py-1 rounded">{task.module}</span>
                      <span>{task.completedAt.toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CompletedTasksPage;
